import React, { Component } from 'react';
import {
  Tabs,
  Tab,
  TextField,
  Button,
} from '@mui/material';
import { cloneDeep } from 'lodash';
import { FormattedMessage } from 'react-intl';

import SurveySection from '../survey/SurveySection';
import { createArray } from '../../utilities/arrayHelper';
import CommentSelector from './reviewCategory/textblockReview/CommentSelector';
import {
  Survey,
  Review as ReviewType,
  ReviewChanges,
  Evaluation,
  Submission,
  ExtendedReview,
  ReviewSurvey,
  ExtendedPayload,
} from '../../redux/types';

type SurveySectionType = Survey['payload']['sections'][0];

export type SubmitReviewData = {
  sections: ReviewType['payload']['sections'];
  multiCommentReview: ExtendedPayload['sections'];
  justification: ReviewType['justification'];
  sectionJustifications: ReviewType['sectionJustifications'];
  reviewChanges: ReviewChanges;
  version: number;
}
type AnswerType = {
  type: string;
  answer: string;
};

type QuestionDetails = {
  sectionIndex: number;
  questionUuid: string;
  sampleIndex: number;
}

type MultiCommentSection = ExtendedPayload['sections'][0];

type Props = {
  survey: Survey['payload'];
  reviewSubmission?: ReviewType;
  onSubmitReview?: (reviewData: SubmitReviewData) => void;
  onCancelReview?: () => void;
  currentSection?: number;
  onCurrentSectionChange?: (sectionId: number) => void;
  evaluation: Evaluation;
  submission: Submission;
  disableReview?: boolean;
  readOnly?: boolean;
  excludedQuestions?: unknown[];
  printFlag?: boolean;
  extendedReview?: ExtendedReview;
  reviewSurvey?: ReviewSurvey;
}

type State = {
  reviewSections: ReviewType['payload']['sections'];
  justification?: ReviewType['justification'];
  multiCommentState: ExtendedPayload['sections'];
  reviewChanges: ReviewChanges;
  [key: string]: unknown;
}
export default class Review extends Component<Props, State> {
  // https://smartinmedia.atlassian.net/browse/QZ-420
  // addMe property is to handle both normal and faerbung review score
  // check the link for more details
  static createEmptyReviewSectionSubmission = (
    surveySection: SurveySectionType,
    addMe?: unknown,
  ) => surveySection.questions.reduce((acc, question) => ({
    ...acc,
    questions: {
      ...acc.questions,
      [question.question.uuid]: {
        addMe,
        answers: createArray(surveySection.numCases).map(() => null),
      },
    },
  }), {} as any);

  constructor(props: Props) {
    super(props);
    this.state = {
      reviewSections: [],
      justification: undefined,
      multiCommentState: [],
      reviewChanges: {},
    };
  }

  componentDidMount(): void {
    this.updateView(this.props);
  }

  componentDidUpdate(prevProps: Props): void {
    const { reviewSubmission } = this.props;
    const { reviewSubmission: reviewSubmissionOld } = prevProps;
    if (reviewSubmission !== undefined
      && reviewSubmission !== reviewSubmissionOld
      && reviewSubmission.payload?.sections) {
      const { extendedPayload } = reviewSubmission;
      if (extendedPayload && extendedPayload.sections) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ multiCommentState: extendedPayload.sections });
      }
      this.updateView(this.props);
    }
  }

  handleFaerbungReview(reviewSurvey: Survey['payload'], thisSection: number): React.ReactNode {
    const { printFlag, excludedQuestions } = this.props;
    const { reviewSections } = this.state;

    const reviews = reviewSurvey.sections.map(
      (section, sectionIndex) => this.createReviewSectionPayload(
        section, sectionIndex, false,
      ),
    );

    return (
      <SurveySection
        key={`FaerbungReview-${thisSection}`}
        payload={reviews[thisSection]}
        sampleIdsNecessary={false}
        sectionSubmission={reviewSections[thisSection]}
        excludedQuestionSection={
          excludedQuestions && excludedQuestions[thisSection]
        }
        startCase={
          reviews
            .slice(0, thisSection)
            .reduce((p, c) => {
              if (c) {
                return p + c.numCases;
              }
              return p;
            }, 0)
        }
        onSamplePayloadChanged={
          (
            questionUuid: string,
            sampleIndex: number,
            payload: AnswerType,
          ) => this.updateReviewSection(
            thisSection, questionUuid, sampleIndex, payload,
          )
        }
        printFlag={printFlag}
      />
    );
  }

  onSelectMultiComment = (commentData: MultiCommentSection): void => {
    const { currentSection = 0 } = this.props;
    const { multiCommentState } = this.state;
    multiCommentState[currentSection] = commentData;
    this.setState({ multiCommentState });
  };

  createReviewSectionPayload = (
    section: SurveySectionType,
    sectionIndex: number,
    surveyIdsMatchReviewSurvey: boolean,
  ) => {
    const {
      evaluation,
      submission,
      currentSection = 0,
      readOnly = false,
    } = this.props;
    const { contextQuestions, ...others } = section;
    const { rawScores } = evaluation;
    const sectionSubmission = submission.payload
      && submission.payload.sections
      && submission.payload.sections[currentSection];
    return {
      ...others,
      questions: section.questions.map((question, questionIdx) => {
        const placeholder = (sampleIdx: number) => {
          const answer = sectionSubmission
            && sectionSubmission.questions[question.question.uuid]
            && sectionSubmission.questions[question.question.uuid].answers[sampleIdx];

          if (question.type !== 'ArbitraryString') {
            return 0;
          }

          return answer && answer.type === 'ArbitraryString' ? answer.answer : '';
        };
        const decorator = surveyIdsMatchReviewSurvey
          && ((sampleIdx: number, currentValue: unknown) => {
            if (!rawScores) {
              return undefined;
            }
            const score = Number(rawScores[sectionIndex][questionIdx][sampleIdx]);
            return {
              label: `${score + (Number(currentValue) || 0)} Pt`,
              floatingLabelStyle: {
                // eslint-disable-next-line no-nested-ternary
                color: Number(currentValue)
                  ? 'darkkhaki'
                  // eslint-disable-next-line no-restricted-globals
                  : ((score === 0 || isNaN(score)) ? 'red' : 'green'),
              },
            };
          });
        return {
          readonly: readOnly,
          disabled: question.excludeFromMarking,
          type: 'ArbitraryString',
          question: question.question,
          placeholder,
          decorator,
        };
      }),
    };
  };

  updateChanges = (
    before: Partial<AnswerType> | undefined | null,
    after: Partial<AnswerType>,
    questionDetails: QuestionDetails,
  ): void => {
    let simplifyAfter = { ...after };
    if (!after.answer || !parseInt(after.answer, 10)) {
      simplifyAfter = {};
    }
    const { reviewChanges }: { reviewChanges: any } = this.state;

    const changesMade = { ...reviewChanges };
    const { sectionIndex, questionUuid, sampleIndex } = questionDetails;
    const isChanged = JSON.stringify(before) !== JSON.stringify(simplifyAfter);
    const currentQuestion = changesMade
      && changesMade[sectionIndex]
      && changesMade[sectionIndex].questions
      && changesMade[sectionIndex].questions[questionUuid]
      && changesMade[sectionIndex].questions[questionUuid].caseNumber
      && changesMade[sectionIndex].questions[questionUuid].caseNumber[sampleIndex];

    if (isChanged) {
      if (!changesMade[sectionIndex]) {
        changesMade[sectionIndex] = { questions: {} };
      }

      if (!changesMade[sectionIndex].questions[questionUuid]) {
        changesMade[sectionIndex].questions[questionUuid] = { caseNumber: {} };
      }

      changesMade[sectionIndex].questions[questionUuid].caseNumber[sampleIndex] = {
        before, after,
      };
    }

    if (!isChanged && currentQuestion) {
      delete changesMade[sectionIndex].questions[questionUuid].caseNumber[sampleIndex];
      if (!Object.keys(changesMade[sectionIndex].questions[questionUuid].caseNumber).length) {
        delete changesMade[sectionIndex].questions[questionUuid];
      }
      if (!Object.keys(changesMade[sectionIndex].questions).length) {
        delete changesMade[sectionIndex].questions;
      }
      if (!Object.keys(changesMade[sectionIndex]).length) {
        delete changesMade[sectionIndex];
      }
    }

    this.setState({ reviewChanges: changesMade });
  };

  updateReviewSection = (
    sectionIndex: number,
    questionUuid: string,
    sampleIndex: number,
    payload: AnswerType,
    forceAddToRawScore?: boolean,
  ): void => {
    const { survey: surveyPayload, reviewSurvey, reviewSubmission } = this.props;
    const { reviewSections } = this.state;
    const surveySection = surveyPayload.sections[sectionIndex];

    let reviewSurveySection;
    if (reviewSurvey) {
      reviewSurveySection = reviewSurvey.sections[sectionIndex];
    }

    const reviewSection = reviewSections[sectionIndex];
    // the initial point creating payload
    const modifiedSection = reviewSection
      ? cloneDeep(reviewSection)
      : Review.createEmptyReviewSectionSubmission({ ...surveySection, ...reviewSurveySection });

    if (!modifiedSection.questions[questionUuid] && forceAddToRawScore) {
      const newmodifiedSection = Review.createEmptyReviewSectionSubmission(
        surveySection, forceAddToRawScore,
      );
      modifiedSection.questions = { ...modifiedSection.questions, ...newmodifiedSection.questions };
    }

    modifiedSection.questions[questionUuid].answers[sampleIndex] = payload;
    let before: Partial<AnswerType> | null | undefined;
    // eslint-disable-next-line max-len
    const reviewAnswer = reviewSubmission?.payload?.sections[sectionIndex]?.questions[questionUuid]?.answers[sampleIndex]?.answer;
    if (reviewAnswer && parseInt(reviewAnswer, 10) && reviewSubmission) {
      before = reviewSubmission.payload.sections[sectionIndex]?.questions[questionUuid]
        .answers[sampleIndex];
    }
    if (!before) {
      before = {};
    }
    this.updateChanges(before, payload, { sectionIndex, questionUuid, sampleIndex });

    this.setState({
      reviewSections:
        Object.assign([], reviewSections, { [sectionIndex]: modifiedSection }),
    });
  };

  updateView(nextProps: Props): void {
    if (nextProps.reviewSubmission) {
      const sectionJustifactions = nextProps.reviewSubmission.sectionJustifications
        && nextProps.reviewSubmission.sectionJustifications.reduce((all, sectionJustification, idx) => ({ ...all, [`sectionJustification.${idx}`]: sectionJustification }), {});

      const newState = {
        reviewSections: nextProps.reviewSubmission.payload.sections,
        justification: nextProps.reviewSubmission.justification,
        ...sectionJustifactions,
      };
      this.setState(newState);
    }
  }

  renderSectionsAsTabs = (sections: SurveySectionType[]): React.ReactNode => {
    const {
      currentSection,
      extendedReview,
      reviewSurvey,
      printFlag,
      onCurrentSectionChange,
      excludedQuestions,
      disableReview,
      onSubmitReview,
      onCancelReview,
    } = this.props;
    const {
      multiCommentState,
      reviewChanges,
      reviewSections,
      justification,
    } = this.state;
    if (currentSection === undefined) {
      return null;
    }
    const startCase = sections
      .slice(0, currentSection)
      .reduce((p, c) => (p + c.numCases), 0);
    const selectedSection = sections[currentSection];
    return (
      <div>
        {
          !printFlag && sections.length > 1 && (
            <Tabs value={currentSection} indicatorColor="secondary" variant="scrollable" scrollButtons="auto">
              {
                sections.map((section, sectionIndex) => (
                  <Tab
                    // eslint-disable-next-line react/no-array-index-key
                    key={`ReviewSectionTab-${sectionIndex}`}
                    label={section.headline || `Section ${sectionIndex + 1}`}
                    onClick={() => {
                      if (onCurrentSectionChange) {
                        onCurrentSectionChange(sectionIndex);
                      }
                    }}
                    value={sectionIndex}
                  />
                ))
              }
            </Tabs>
          )
        }
        <SurveySection
          key={`ReviewSectionSection-${currentSection}`}
          payload={selectedSection}
          sampleIdsNecessary={false}
          sectionSubmission={reviewSections[currentSection]}
          excludedQuestionSection={
            excludedQuestions && excludedQuestions[currentSection]
          }
          startCase={startCase}
          onSamplePayloadChanged={
            (
              questionUuid: string,
              sampleIndex: number,
              payload: AnswerType,
            ) => this.updateReviewSection(
              currentSection, questionUuid, sampleIndex, payload, true,
            )
          }
          printFlag={printFlag}
        />
        {
          reviewSurvey
          && (this.handleFaerbungReview(reviewSurvey, currentSection))
        }
        {
          extendedReview
          && extendedReview.data[`section${currentSection + 1}`]
          && (
            <CommentSelector
              data={extendedReview.data[`section${currentSection + 1}`]}
              sectionHeading={sections[currentSection].headline}
              onSelect={this.onSelectMultiComment}
              selectedValues={multiCommentState[currentSection] || {}}
            />
          )
        }
        {
          !disableReview && (
            <div style={{ marginBottom: 10 }}>
              <TextField
                sx={{ width: '100%' }}
                // wip
                // placeholder={<FormattedMessage id="review.sectionJustificationHeading" />}
                label={<FormattedMessage id="review.sectionJustificationHeading" />}
                onChange={(e) => this.setState({ [`sectionJustification.${currentSection}`]: e.target.value })}
                value={this.state[`sectionJustification.${currentSection}`] || ''}
                multiline
                variant="standard"
              />
            </div>
          )
        }
        {!disableReview
          && currentSection === 0 && (
            <div style={{ marginBottom: 10 }}>
              <TextField
                sx={{ width: '100%' }}
                // wip
                // placeholder={<FormattedMessage id="review.justificationHeading" />}
                label={<FormattedMessage id="review.justificationHeading" />}
                onChange={(e) => this.setState({ justification: e.target.value })}
                value={justification || ''}
                multiline
                variant="standard"
              />
            </div>
        )}
        {
          onSubmitReview && onCancelReview && (
            <div
              style={{
                textAlign: 'right',
                marginTop: 10,
              }}
            >
              <Button
                sx={{ m: 1 }}
                variant="contained"
                color="primary"
                onClick={() => onSubmitReview({
                  version: 1,
                  sections: reviewSections,
                  justification: justification || '',
                  sectionJustifications: sections.map((_, idx) => this.state[`sectionJustification.${idx}`] as string),
                  multiCommentReview: multiCommentState,
                  reviewChanges,
                })}
              >
                <FormattedMessage id="review.submitReviewSubmission" />
              </Button>
              <Button
                sx={{ m: 1 }}
                variant="contained"
                color="secondary"
                onClick={onCancelReview}
              >
                <FormattedMessage id="review.cancelReviewSubmission" />
              </Button>
            </div>
          )
        }
      </div>
    );
  };

  render(): React.ReactNode {
    const { survey: surveyPayload } = this.props;

    if (!surveyPayload) {
      return false;
    }

    const reviewSurveySections = surveyPayload.sections.map(
      (section, sectionIndex) => this.createReviewSectionPayload(
        section, sectionIndex, true,
      ),
    );
    return (
      <div>
        {
          this.renderSectionsAsTabs(reviewSurveySections as any)
        }
      </div>
    );
  }
}
